import { FirewallIndicatorState } from '../types';

export default function FirewallIndicator() {
  return {
    init() {
      this.fetchStatus();
      // Poll for status updates every 30 seconds
      setInterval(() => this.fetchStatus(), 30000);

      // Update relative time display every second
      setInterval(() => {
        if (this.state.showTooltip && this.state.lastInstallation) {
          // Force re-render of tooltip content
          document.dispatchEvent(new CustomEvent('firewall:time-update'));
        }
      }, 1000);

      // Listen for refresh events
      document.addEventListener('firewall:refresh-events', () => {
        this.fetchStatus();
      });

      // Listen for time update events
      document.addEventListener('firewall:time-update', () => {
        // This will trigger Alpine.js to re-evaluate the tooltipText getter
        this.state = { ...this.state };
      });
    },

    state: {
      isProtected: false,
      isLoading: true,
      error: null,
      lastInstallation: null,
      showTooltip: false,
      lastStatus: null,
      lastInstallationTime: null
    } as FirewallIndicatorState,

    async fetchStatus() {
      try {
        if (this.state.lastStatus === null) {
          this.state.isLoading = true;
        }
        this.state.error = null;

        const response = await fetch('/firewall/api/user-status/');
        if (!response.ok) {
          throw new Error('Failed to fetch firewall status');
        }

        const data = await response.json();
        const newStatus = data.is_protected;
        const newInstallation = data.last_installation;

        // Check if protection status changed
        if (this.state.lastStatus !== newStatus) {
          this.state.isProtected = newStatus;
          this.state.lastStatus = newStatus;
        }

        // Check if installation timestamp changed
        if (this.state.lastInstallationTime !== newInstallation) {
          this.state.lastInstallation = newInstallation;
          this.state.lastInstallationTime = newInstallation;

          // Force UI refresh by dispatching a custom event if needed
          if (newInstallation) {
            document.dispatchEvent(new CustomEvent('firewall:timestamp-updated'));
          }
        }
      } catch (error) {
        this.state.error = error instanceof Error ? error.message : 'An error occurred';
      } finally {
        this.state.isLoading = false;
      }
    },

    get statusText() {
      if (this.state.error) {
        return 'Error';
      }
      return this.state.isProtected ? 'Protected by Firewall' : 'Firewall Not Active';
    },

    get statusClass() {
      if (this.state.error) {
        return 'text-[#ef4444] dark:text-red-500';
      }
      return this.state.isProtected ? 'text-[#15803d] dark:text-emerald-400' : 'text-[#b45309] dark:text-amber-500';
    },

    getRelativeTime(dateString: string): string {
      const date = new Date(dateString);
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

      if (diffInSeconds < 60) {
        return 'just now';
      }

      const diffInMinutes = Math.floor(diffInSeconds / 60);
      if (diffInMinutes < 60) {
        return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
      }

      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) {
        return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
      }

      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 30) {
        return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
      }

      const diffInMonths = Math.floor(diffInDays / 30);
      if (diffInMonths < 12) {
        return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
      }

      const diffInYears = Math.floor(diffInMonths / 12);
      return `${diffInYears} year${diffInYears === 1 ? '' : 's'} ago`;
    },

    get tooltipText() {
      if (this.state.error) {
        return 'Error loading Firewall status';
      }
      if (this.state.isProtected) {
        if (this.state.lastInstallation) {
          return `Safety Firewall is protecting your package installations.<br>Last package installed ${this.getRelativeTime(this.state.lastInstallation)}.`;
        }
        return 'Safety Firewall is protecting your package installations.';
      }
      return 'Run \'safety init\' to enable Firewall protection';
    },

    showTooltip() {
      this.state.showTooltip = true;
    },

    hideTooltip() {
      this.state.showTooltip = false;
    },

    handleClick() {
      if (this.state.isProtected) {
        window.location.href = '/firewall';
      } else {
        window.location.href = '/get-started';
      }
    }
  };
}
